// qs-page-DrawingPage.vue
<template>
  <div class="drawing-page w-full h-full relative">
    <AppLoadingScreen v-if="!isAppReady" />

    <template v-else>
      <DrawingGallery
        :is-open="galleryStore.isSidebarOpen"
        @drawing-selected="handleDrawingSelected"
        @close="closeGallery"
      />

      <div
        class="canvas-wrapper"
        :class="{ 'canvas-wrapper-with-sidebar': galleryStore.isSidebarOpen }"
      >
        <Canvas />
      </div>
    </template>

    <div class="panels-layer">
      <DraggablePanel panel-id="file">
        <FilePanel
          @new-file="handleNewFile"
          @open-file="handleOpenFile"
          @duplicate-file="handleDuplicateFile"
          @export-file="handleExportFile"
          @export-png="handleExportPNG"
          @export-svg="handleExportSVG"
          @export-zip="handleExportZip"
          @import-zip="handleImportZip"
          @reset-cache="handleResetCache"
          @show-info="handleShowInfo"
        />
      </DraggablePanel>

      <DraggablePanel panel-id="colors">
        <ColorsPanel
          @color-selected="handleColorSelected"
          @color-edited="handleColorEdited"
          @panel-hover="handlePanelHover"
        />
      </DraggablePanel>

      <DraggablePanel panel-id="brush-size">
        <BrushSizePanel
          :brush-count="5"
          :initial-sizes="BRUSH_SIZES"
          :max-items="5"
          @brush-size-changed="handleBrushSizeChanged"
          @brush-item-selected="handleBrushItemSelected"
        />
      </DraggablePanel>

      <DraggablePanel panel-id="brush-type">
        <BrushTypePanel
          :current-brush-type="currentBrushType"
          @brush-type-changed="handleBrushTypeChanged"
        />
      </DraggablePanel>

      <DraggablePanel panel-id="smooth">
        <SmoothPanel
          @smoothing-mode-changed="handleSmoothingModeChanged"
        />
      </DraggablePanel>

      <DraggablePanel panel-id="layers">
        <LayersPanel
          @layer-selected="handleLayerSelected"
          @layer-added="handleLayerAdded"
          @layer-deleted="handleLayerDeleted"
          @layer-visibility-changed="handleLayerVisibilityChanged"
          @layer-lock-changed="handleLayerLockChanged"
          @background-color-changed="handleBackgroundColorChanged"
        />
      </DraggablePanel>

      <DraggablePanel panel-id="navigation">
        <NavigationPanel
          @delete-mode-changed="handleDeleteModeChanged"
          @performance-toggle="handlePerformanceToggle"
        />
      </DraggablePanel>

      <DraggablePanel panel-id="mode">
        <ModePanel />
      </DraggablePanel>

      <DraggablePanel panel-id="performance">
        <PerformancePanel />
      </DraggablePanel>

      <DraggablePanel panel-id="text">
        <TextPanel
          @text-style-selected="handleTextStyleSelected"
          @text-placement-mode="handleTextPlacementMode"
        />
      </DraggablePanel>
    </div>

    <ResetWarningModal
      :is-visible="showResetModal"
      @cancel="handleResetCancel"
      @confirm-reset="handleResetConfirm"
    />

    <InfoModal
      :is-visible="showInfoModal"
      @close="handleInfoClose"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import BrushSizePanel from '@/components/panels/qs-BrushSizePanel.vue'
import BrushTypePanel from '@/components/panels/qs-BrushTypePanel.vue'
import ColorsPanel from '@/components/panels/qs-ColorsPanel.vue'
import SmoothPanel from '@/components/panels/qs-SmoothPanel.vue'
import FilePanel from '@/components/panels/qs-FilePanel.vue'
import LayersPanel from '@/components/panels/qs-LayersPanel.vue'
import ModePanel from '@/components/panels/qs-ModePanel.vue'
import NavigationPanel from '@/components/panels/qs-NavigationPanel.vue'
import PerformancePanel from '@/components/panels/qs-PerformancePanel.vue'
import TextPanel from '@/components/panels/qs-TextPanel.vue'
import Canvas from '@/components/qs-Canvas-Main.vue'
import AppLoadingScreen from '@/components/ui/qs-AppLoadingScreen.vue'
import DraggablePanel from '@/components/ui/qs-DraggablePanel.vue'
import DrawingGallery from '@/components/ui/qs-DrawingGallery.vue'
import ResetWarningModal from '@/components/ui/qs-ResetWarningModal.vue'
import InfoModal from '@/components/ui/qs-InfoModal.vue'
import { useAppInitialization } from '@/composables/qs-useAppInitialization'
import { useDrawingManagement } from '@/composables/qs-useDrawingManagement'
import { useEventBus } from '@/composables/qs-useEventBus'
import { useExport } from '@/composables/qs-useExport'
import { useFileOperations } from '@/composables/qs-useFileOperations'
import { useHistory } from '@/composables/qs-useHistory'
import { useModeManager } from '@/composables/qs-useModeManager'
import { usePanelHandlers } from '@/composables/qs-usePanelHandlers'
import { useZipExport } from '@/composables/qs-useZipExport'
import { useGalleryStore } from '@/stores/qs-store-gallery'
import { usePanelStore } from '@/stores/qs-store-panels'
import { useToolStore } from '@/stores/qs-store-tools'
import { cursorManager } from '@/utils/qs-cursorManager'
import { BRUSH_SIZES } from '@/config/qs-brushDefaults'

const toolStore = useToolStore()
const galleryStore = useGalleryStore()
const panelStore = usePanelStore()

const modeManager = useModeManager()
const history = useHistory()

const fileOperations = useFileOperations()
const exportUtils = useExport()
const panelHandlers = usePanelHandlers()
const drawingManagement = useDrawingManagement()
const eventBus = useEventBus()
const zipExport = useZipExport()

const isAppReady = ref(false)
const showResetModal = ref(false)
const showInfoModal = ref(false)

const currentBrushType = computed(() => toolStore.currentTool.brushType)

const {
  handleColorSelected,
  handleColorEdited,
  handlePanelHover,
  handleBrushSizeChanged,
  handleBrushItemSelected,
  handleBrushTypeChanged,
  handleSmoothingModeChanged,
  handleLayerSelected,
  handleLayerAdded,
  handleLayerDeleted,
  handleLayerVisibilityChanged,
  handleLayerLockChanged,
  handleBackgroundColorChanged,
  handleDeleteModeChanged
} = panelHandlers

const {
  handleNewFile,
  handleOpenFile,
  handleDuplicateFile
} = fileOperations

const { exportCanvasAsPNG, exportCanvasAsSVG } = exportUtils

const {
  handleDrawingSelected,
  closeGallery,
  saveCurrentDrawing,
  setupAutoSave,
  cleanup
} = drawingManagement

const handleExportFile = () => {
  window.dispatchEvent(new CustomEvent('file-changed'))
  exportCanvasAsPNG()
}

const handleExportPNG = () => {
  window.dispatchEvent(new CustomEvent('file-changed'))
  exportCanvasAsPNG()
}

const handleExportSVG = () => {
  window.dispatchEvent(new CustomEvent('file-changed'))
  exportCanvasAsSVG()
}

const handleExportZip = async () => {
  try {
    await zipExport.downloadExport()
  } catch (error) {
    alert('Export failed: ' + (error instanceof Error ? error.message : 'Unknown error'))
  }
}

const handleImportZip = async () => {
  try {
    const result = await zipExport.handleImportFile()

    if (result.success) {
      alert(`Successfully imported ${result.importedDrawings} drawings!`)
      window.location.reload()
    } else {
      alert('Import failed: ' + result.errors.join(', '))
    }
  } catch (error) {
    alert('Import failed: ' + (error instanceof Error ? error.message : 'Unknown error'))
  }
}

const handleResetCache = () => {
  showResetModal.value = true
}

const handleResetCancel = () => {
  showResetModal.value = false
}

const handleResetConfirm = () => {
  showResetModal.value = false
  localStorage.clear()
  sessionStorage.clear()

  if ('indexedDB' in window) {
    indexedDB.databases().then(databases => {
      databases.forEach(db => {
        if (db.name) {
          indexedDB.deleteDatabase(db.name)
        }
      })
    })
  }

  window.location.reload()
}

const handleShowInfo = () => {
  showInfoModal.value = true
}

const ABOUT_SEEN_KEY = 'qs-hasSeenAbout'

const handleInfoClose = () => {
  showInfoModal.value = false
  try {
    localStorage.setItem(ABOUT_SEEN_KEY, '1')
  } catch {
  }
}


const handlePerformanceToggle = () => {
}

const handleTextStyleSelected = () => {
}

const handleTextPlacementMode = () => {
}

let keyboardCleanup: (() => void) | null = null
let eventBusCleanup: (() => void) | null = null
let drawingCompletedAnimationFrame: number | null = null

const handleDrawingCompleted = () => {
  if (drawingCompletedAnimationFrame) {
    cancelAnimationFrame(drawingCompletedAnimationFrame)
  }
  drawingCompletedAnimationFrame = requestAnimationFrame(() => {
    saveCurrentDrawing()
    drawingCompletedAnimationFrame = null
  })
}

onMounted(async () => {
  const { initializeApp } = useAppInitialization()
  await initializeApp()

  const startTime = Date.now()
  const minDisplayTime = 1000

  await nextTick()
  await new Promise(resolve => setTimeout(resolve, 100))

  const elapsedTime = Date.now() - startTime
  const remainingTime = Math.max(0, minDisplayTime - elapsedTime)

  if (remainingTime > 0) {
    await new Promise(resolve => setTimeout(resolve, remainingTime))
  }

  isAppReady.value = true

  try {
    if (!localStorage.getItem(ABOUT_SEEN_KEY)) {
      showInfoModal.value = true
    }
  } catch {
  }

  nextTick(async () => {
    if (modeManager.activeMode.value !== 'drawing') {
      modeManager.switchToDrawingMode()
    }

    const { cursorManager } = await import('@/utils/qs-cursorManager')
    cursorManager.refreshDrawingCursor()
  })

  setupAutoSave()
  eventBus.on('drawing-completed', handleDrawingCompleted)

  eventBusCleanup = () => {
    eventBus.off('drawing-completed', handleDrawingCompleted)
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (cursorManager.isAnyInputActive()) {
      return
    }

    if (e.ctrlKey || e.metaKey) {
      if (e.key.toLowerCase() === 'z') {
        e.preventDefault()
        if (e.repeat) return
        history.performUndo()
        return
      }
      if (e.key.toLowerCase() === 'y') {
        e.preventDefault()
        if (e.repeat) return
        history.performRedo()
        return
      }
    }

    if (e.key.toLowerCase() === 'k') {
      e.preventDefault()
      panelStore.togglePanel('performance')
    }
  }

  document.addEventListener('keydown', handleKeyDown)

  keyboardCleanup = () => {
    document.removeEventListener('keydown', handleKeyDown)
  }
})

onUnmounted(() => {
  if (drawingCompletedAnimationFrame) {
    cancelAnimationFrame(drawingCompletedAnimationFrame)
    drawingCompletedAnimationFrame = null
  }
  if (keyboardCleanup) {
    keyboardCleanup()
    keyboardCleanup = null
  }
  if (eventBusCleanup) {
    eventBusCleanup()
    eventBusCleanup = null
  }
  cleanup()
})
</script>

<style scoped>
.drawing-page {
  background: transparent;
  overflow: hidden;
}

.canvas-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  transition: left 0.3s ease, width 0.3s ease;
}

.canvas-wrapper.canvas-wrapper-with-sidebar {
  left: 320px;
  width: calc(100vw - 320px);
}

.panels-layer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 10;
}

.panels-layer > * {
  pointer-events: auto;
}
</style>
